import { api, type ApiResult } from '../../api/client.js';
import { adminCommand, operation, operationBatch, uuid } from '../../api/documents.js';
import { SAO_JOAO_SOURCE, saoJoaoRoute, type RoutePoint, type SourcePole } from './sao-joao-route.js';

export { saoJoaoRoute, type RoutePoint };

interface Tenant { id: string; name: string; code: string; status: string }
interface Farm { id: string; name: string; status: string }
interface Tag { id: string; epcNormalized: string }
interface Pole { id: string; tagId: string | null }
interface Pasture { id: string; name: string; status: string }

const TENANT_NAME = 'SÃO JOÃO';
const FARM_NAME = 'FAZENDA SÃO JOÃO';
const SOURCE = SAO_JOAO_SOURCE;

export interface SaoJoaoSeedResult {
  readonly tenant: Tenant;
  readonly farmId: string;
  readonly pastureCount: number;
  readonly poleCount: number;
}

type Progress = (message: string) => void;

function key(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').trim().toUpperCase();
}

export function isSaoJoaoDemo(name: string): boolean {
  return key(name) === key(TENANT_NAME);
}

async function listAll<T>(path: string): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null = null;
  do {
    const separator = path.includes('?') ? '&' : '?';
    const cursorPart: string = cursor === null ? '' : `${separator}cursor=${encodeURIComponent(cursor)}`;
    const result: ApiResult<{ items: T[]; nextCursor: string | null }> =
      await api.list<{ items: T[]; nextCursor: string | null }>(`${path}${cursorPart}`);
    items.push(...(result.body.items ?? []));
    cursor = result.body.nextCursor ?? null;
  } while (cursor !== null);
  return items;
}

function requireApplied(body: Record<string, unknown>, step: string): void {
  if (body.status !== 'applied') {
    throw new Error(`${step}: ${String(body.detail ?? body.errorCode ?? 'pedido recusado')}`);
  }
}

function demoEpc(tenantCode: string, recordedEpc: string): string {
  // Preserva tipo 02 e o identificador legado, trocando apenas o código do
  // cliente para o código realmente alocado pelo servidor.
  return `${tenantCode}${recordedEpc.slice(4)}`;
}

function expansionTagId(index: number): string {
  return `90000000-0000-4000-8000-${index.toString(16).padStart(12, '0')}`;
}

export function saoJoaoSummary(): { pastureCount: number; poleCount: number } {
  return {
    pastureCount: SOURCE.fences.length,
    poleCount: new Set(SOURCE.fences.flatMap((pasture) => pasture.poles.map((pole) => pole.epc))).size,
  };
}

/**
 * Prepara a demonstração usando exclusivamente os endpoints normais.
 *
 * A rotina é retomável: antes de cada grupo consulta o que já existe. Se a aba
 * fechar depois da etiqueta 30, executar novamente continua nas restantes sem
 * criar outro cliente, outra fazenda ou duplicar EPC.
 */
export async function seedSaoJoao(progress: Progress): Promise<SaoJoaoSeedResult> {
  progress('Localizando o cliente São João…');
  let tenants = await listAll<Tenant>('/admin/tenants?limit=100');
  let tenant = tenants.find((item) => key(item.name) === key(TENANT_NAME));

  if (tenant === undefined) {
    const tenantId = uuid();
    progress('Criando o cliente São João…');
    const result = await api.command<Record<string, unknown>>(
      adminCommand(tenantId, 'tenant_create', { name: TENANT_NAME }),
    );
    requireApplied(result.body, 'Criação do cliente');
    tenants = await listAll<Tenant>('/admin/tenants?limit=100');
    tenant = tenants.find((item) => item.id === tenantId);
    if (tenant === undefined) throw new Error('Cliente criado, mas não apareceu na consulta administrativa.');
  }

  const scope = `/admin/tenants/${tenant.id}`;
  progress('Preparando a fazenda São João…');
  let farms = await listAll<Farm>(`${scope}/farms?limit=100`);
  let farm = farms.find((item) => item.status === 'active' && key(item.name) === key(FARM_NAME));
  if (farm === undefined) {
    const farmId = uuid();
    const result = await api.command<Record<string, unknown>>(
      adminCommand(tenant.id, 'farm_create', { farmId, name: FARM_NAME }),
    );
    requireApplied(result.body, 'Criação da fazenda');
    farms = await listAll<Farm>(`${scope}/farms?limit=100`);
    farm = farms.find((item) => item.id === farmId);
    if (farm === undefined) throw new Error('Fazenda criada, mas não apareceu na consulta administrativa.');
  }

  const uniquePoles = new Map<string, SourcePole>();
  for (const pasture of SOURCE.fences) {
    for (const pole of pasture.poles) uniquePoles.set(pole.epc, pole);
  }

  let tags = await listAll<Tag>(`${scope}/tags?limit=100`);
  const tagByEpc = new Map(tags.map((tag) => [tag.epcNormalized, tag]));
  const missingPoles: { recorded: SourcePole; tagId: string }[] = [];
  let expansionIndex = 1;
  for (const recorded of uniquePoles.values()) {
    const epc = demoEpc(tenant.code, recorded.epc);
    if (!tagByEpc.has(epc)) {
      missingPoles.push({ recorded, tagId: expansionTagId(expansionIndex++) });
    }
  }
  if (missingPoles.length > 0) {
    progress(`Cadastrando ${missingPoles.length} etiquetas de poste em lote…`);
    const requestId = '90000000-0000-4000-8001-000000000001';
    const result = await api.command<{
      status?: unknown;
      generated?: { tags?: { tagId: string; epcRaw: string }[] };
      detail?: unknown;
      errorCode?: unknown;
    }>({
      schemaVersion: 'rfid.mvp.admin-command/2.1.0',
      requestId,
      actor: { kind: 'demo_admin', subject: `demo-admin:${tenant.id}` },
      occurredAtMs: Date.now(),
      command: {
        kind: 'tag_batch_create', tenantId: tenant.id, tagType: 'pole',
        tagIds: missingPoles.map((item) => item.tagId),
      },
    });
    requireApplied(result.body as Record<string, unknown>, 'Etiquetas de poste');
    const generated = result.body.generated?.tags ?? [];
    if (generated.length !== missingPoles.length) throw new Error('Lote de postes retornou cardinalidade divergente.');
    generated.forEach((tag, index) => {
      const recorded = missingPoles[index]!.recorded;
      tagByEpc.set(demoEpc(tenant.code, recorded.epc), { id: tag.tagId, epcNormalized: tag.epcRaw });
    });
  }

  tags = await listAll<Tag>(`${scope}/tags?limit=100`);
  for (const tag of tags) tagByEpc.set(tag.epcNormalized, tag);
  const [pastures, poles] = await Promise.all([
    listAll<Pasture>(`${scope}/pastures?limit=100`),
    listAll<Pole>(`${scope}/poles?limit=100`),
  ]);
  const activePastureNames = new Set(
    pastures.filter((pasture) => pasture.status === 'active').map((pasture) => key(pasture.name)),
  );
  const poleByTagId = new Map(
    poles.filter((pole) => pole.tagId !== null).map((pole) => [pole.tagId!, pole.id]),
  );

  const assignedPoleIds = new Map<string, string>();
  for (const [recordedEpc] of uniquePoles) {
    const tag = tagByEpc.get(demoEpc(tenant.code, recordedEpc));
    const existingPoleId = tag === undefined ? undefined : poleByTagId.get(tag.id);
    if (existingPoleId !== undefined) assignedPoleIds.set(recordedEpc, existingPoleId);
  }

  const operations: Record<string, unknown>[] = [];
  for (const pasture of SOURCE.fences) {
    if (activePastureNames.has(key(pasture.name))) continue;
    const perimeter = [...pasture.poles]
      .sort((a, b) => a.order - b.order)
      .map((recorded, ordinal) => {
        const existingPoleId = assignedPoleIds.get(recorded.epc);
        if (existingPoleId !== undefined) return { ordinal, poleId: existingPoleId };

        const tag = tagByEpc.get(demoEpc(tenant.code, recorded.epc));
        if (tag === undefined) throw new Error(`Etiqueta do poste ${recorded.epc} não foi encontrada.`);
        const poleId = uuid();
        assignedPoleIds.set(recorded.epc, poleId);
        return {
          ordinal,
          poleId,
          newPole: {
            coordinate: {
              latitude: recorded.latitude,
              longitude: recorded.longitude,
              origin: 'MANUAL_MAP',
              accuracyMeters: null,
            },
            tagId: tag.id,
            bindingId: uuid(),
            confirmation: { method: 'ADMIN_SELECTION', confirmedCode: tag.epcNormalized },
          },
        };
      });
    operations.push(operation(tenant.id, farm.id, 'pasture_created', {
      kind: 'pasture_created',
      // O UUID legado identifica o arquivo antigo, não é identidade global da
      // nova plataforma. Cada carga nova recebe um ID real e evita colisão com
      // outra base que também tenha importado aquele mock.
      pastureId: uuid(),
      farmId: farm.id,
      name: pasture.name,
      perimeter,
    }));
  }

  if (operations.length > 0) {
    progress(`Criando ${operations.length} pastos e vinculando os postes…`);
    const result = await api.operations<Record<string, unknown>>(await operationBatch(tenant.id, operations));
    const rejected = ((result.body.results ?? []) as Record<string, unknown>[]).find(
      (item) => item.state !== 'applied',
    );
    if (rejected !== undefined) {
      throw new Error(`Criação dos pastos: ${String(rejected.detail ?? rejected.errorCode ?? rejected.state)}`);
    }
  }

  progress('Demonstração São João pronta.');
  return {
    tenant,
    farmId: farm.id,
    pastureCount: SOURCE.fences.length,
    poleCount: uniquePoles.size,
  };
}
